import { translate } from '@jsverse/transloco';
import {
  createEffect,
  dispatch,
  ofType,
  registerEffects,
  removeEffects,
  tapResult,
} from '@ngneat/effects';
import {
  AccountsApi,
  FleetStatsApi,
  UserPagedCollectionParams,
} from '@tmx/data-access';
import {
  combineLatest,
  distinctUntilChanged,
  forkJoin,
  switchMap,
  tap,
} from 'rxjs';

import { hasMapSets$ } from '../../features/cfs/features/maps-sets/repositories/map-sets.repository';
import { hasRoutingProfiles$ } from '../../features/cfs/features/routing-profiles/repositories/routing-profiles.repository';
import { hasVehicleGroups$ } from '../../features/cfs/features/vehicle-groups/repositories/vehicle-groups.repository';
import { getErrorContext } from '../../utils/get-error-context';
import { reportToTrackjs } from '../../utils/report-to-trackjs';
import {
  hasAssignedLicenses$,
  hasInvitedDrivers$,
} from '../assets/assets.repository';
import {
  completeItineraryItem,
  initializeOnboardingTourChecklist,
  onboardingTourChecklistInitialized,
  setOnboardingInitialStats,
} from './onboarding-tour.actions';
import {
  completeItineraryByName$,
  updateInitialStats,
} from './onboarding-tour.repository';

const TRACKJS_SOURCE = 'onboarding-tour.effects';

const initializeOnboardingTour$ = createEffect((actions$) =>
  actions$.pipe(
    ofType(initializeOnboardingTourChecklist),
    switchMap(() =>
      forkJoin({
        accountStats: FleetStatsApi.getDashboardAccountStats(),
        routingProfileStats: FleetStatsApi.getDashboardRoutingProfileStats(),
        users: AccountsApi.getUsersFleetInfo(
          new UserPagedCollectionParams({
            offset: 0,
            limit: 1,
            userStatus: 'Invited',
          })
        ),
      }).pipe(
        tapResult(
          ({ accountStats, routingProfileStats, users }) => {
            dispatch(
              setOnboardingInitialStats({ accountStats, routingProfileStats })
            );

            if (accountStats.assetHasLicense)
              dispatch(
                completeItineraryItem({
                  itineraryName: translate(
                    'accountManagerTour.assetsTour.itineraryTitle'
                  ),
                })
              );
            if (accountStats.hasVehicleGroup === true)
              dispatch(
                completeItineraryItem({
                  itineraryName: translate(
                    'accountManagerTour.vehicleGroupsTour.itineraryTitle'
                  ),
                })
              );
            if (accountStats.hasMapset)
              dispatch(
                completeItineraryItem({
                  itineraryName: translate(
                    'accountManagerTour.mapSetTour.itineraryTitle'
                  ),
                })
              );
            if (routingProfileStats.totalCount > 0)
              dispatch(
                completeItineraryItem({
                  itineraryName: translate(
                    'accountManagerTour.routingProfilesTour.itineraryTitle'
                  ),
                })
              );
            if (users.size > 0) {
              dispatch(
                completeItineraryItem({
                  itineraryName: translate(
                    'accountManagerTour.inviteDriversTour.itineraryTitle'
                  ),
                })
              );
            }
            dispatch(onboardingTourChecklistInitialized());
          },
          (error) => {
            reportToTrackjs(
              TRACKJS_SOURCE,
              'Error loading initial onboarding stats',
              getErrorContext(error)
            );
          }
        )
      )
    )
  )
);

const setOnboardingTourStats$ = createEffect((actions$) =>
  actions$.pipe(
    ofType(setOnboardingInitialStats),
    tap(({ accountStats, routingProfileStats }) => {
      updateInitialStats(accountStats, routingProfileStats);
    })
  )
);

const syncChecklistWithObservables$ = createEffect((actions$) =>
  actions$.pipe(
    ofType(onboardingTourChecklistInitialized),
    switchMap(() =>
      combineLatest([
        hasAssignedLicenses$.pipe(distinctUntilChanged()),
        hasVehicleGroups$.pipe(distinctUntilChanged()),
        hasRoutingProfiles$.pipe(distinctUntilChanged()),
        hasMapSets$.pipe(distinctUntilChanged()),
        hasInvitedDrivers$.pipe(distinctUntilChanged()),
      ])
    ),
    tap(([hasAssigned, hasGroups, hasProfiles, hasMapSets, hasInvited]) => {
      if (hasAssigned)
        dispatch(
          completeItineraryItem({
            itineraryName: translate(
              'accountManagerTour.assetsTour.itineraryTitle'
            ),
          })
        );
      if (hasGroups)
        dispatch(
          completeItineraryItem({
            itineraryName: translate(
              'accountManagerTour.vehicleGroupsTour.itineraryTitle'
            ),
          })
        );
      if (hasProfiles)
        dispatch(
          completeItineraryItem({
            itineraryName: translate(
              'accountManagerTour.routingProfilesTour.itineraryTitle'
            ),
          })
        );
      if (hasMapSets)
        dispatch(
          completeItineraryItem({
            itineraryName: translate(
              'accountManagerTour.mapSetTour.itineraryTitle'
            ),
          })
        );
      if (hasInvited)
        dispatch(
          completeItineraryItem({
            itineraryName: translate(
              'accountManagerTour.inviteDriversTour.itineraryTitle'
            ),
          })
        );
    })
  )
);

const completeItineraryItem$ = createEffect((actions$) =>
  actions$.pipe(
    ofType(completeItineraryItem),
    tap(({ itineraryName }) => {
      completeItineraryByName$.next(itineraryName);
    })
  )
);

const effects = [
  initializeOnboardingTour$,
  setOnboardingTourStats$,
  syncChecklistWithObservables$,
  completeItineraryItem$,
];

export function registerOnboardingTourEffects() {
  registerEffects(effects);
}

export function deregisterOnboardingTourEffects() {
  removeEffects(effects);
}
